/**
 * adminService.js — Admin API calls for user management, settings, quota, monitoring.
 *
 * All endpoints require a Bearer token (passed via getAuthHeaders()).
 */

const MANAGER_BASE = '/api/tenant';

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function handleResponse(response) {
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed (${response.status})`);
  }
  return response.json();
}

// ═══════════════════════════════════════════════════════════════════
// USER MANAGEMENT
// ═══════════════════════════════════════════════════════════════════

export async function listUsers() {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/`, { headers: getAuthHeaders() }));
}

export async function createUser(userData) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/`, {
    method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(userData),
  }));
}

export async function updateUser(userId, userData) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/${userId}`, {
    method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(userData),
  }));
}

export async function deleteUser(userId) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/${userId}`, {
    method: 'DELETE', headers: getAuthHeaders(),
  }));
}

// Set the account's active state. Declarative on purpose: the old
// POST /{id}/toggle-active let the server flip whatever it found, so a
// double-click flipped twice and landed back where it started. Say the
// state you want instead.
export async function setUserActive(userId, isActive) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/${userId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ is_active: isActive }),
  }));
}

// Approve or reject a pending account (was two endpoints until 2026-08-19).
export async function setUserApproval(userId, status) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/${userId}/approval`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status }),
  }));
}

// ═══════════════════════════════════════════════════════════════════
// VALIDATION POLICY & ADMIN SETTINGS
// ═══════════════════════════════════════════════════════════════════

export async function getValidationPolicy() {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/validation-policy/`, { headers: getAuthHeaders() }));
}

export async function updateValidationPolicy(data) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/validation-policy/`, {
    method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
  }));
}

export async function getAllowedDomains() {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/admin/allowed-domains`, { headers: getAuthHeaders() }));
}

export async function updateAllowedDomains(domains) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/admin/allowed-domains`, {
    method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ domains }),
  }));
}

export default {
  listUsers, createUser, updateUser, deleteUser,
  setUserActive, setUserApproval,
  getValidationPolicy, updateValidationPolicy, getAllowedDomains, updateAllowedDomains,
};
